import { Component } from '@theme/component';

/**
 * A decorative eye overlay that points its pupils toward the active pointer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} eyes - The eye sockets rendered over the image.
 * @property {HTMLElement[]} pupils - The pupils rendered inside each eye socket.
 *
 * @extends {Component<Refs>}
 */
class CursorFollowEyesComponent extends Component {
  requiredRefs = ['eyes', 'pupils'];

  static pointerTravelMultiplier = 0.32;

  #abortController = new AbortController();

  #resizeObserver;

  #layoutFrame = 0;

  #renderFrame = 0;

  #pointerX = window.innerWidth / 2;

  #pointerY = window.innerHeight / 2;

  #activeTouchPointerId = null;

  #trackingEnabled = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    super.connectedCallback();
    this.#queueLayoutUpdate();

    const signal = this.#abortController.signal;
    const mediaBlock = this.closest('.media-block');
    const image = this.#image;

    if (image instanceof HTMLImageElement) {
      if ('ResizeObserver' in window) {
        this.#resizeObserver = new ResizeObserver(() => this.#queueLayoutUpdate());
        this.#resizeObserver.observe(image);
      }

      if (!image.complete) {
        image.addEventListener('load', this.#queueLayoutUpdate, { once: true, signal });
      }
    }

    window.addEventListener('resize', this.#queueLayoutUpdate, { passive: true, signal });

    if (!this.#trackingEnabled) {
      return;
    }

    window.addEventListener('pointermove', this.#handlePointerMove, { passive: true, signal });
    window.addEventListener('pointerup', this.#handlePointerUp, { passive: true, signal });
    window.addEventListener('pointercancel', this.#handlePointerUp, { passive: true, signal });
    window.addEventListener('blur', this.#resetPupils, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    mediaBlock?.addEventListener('pointerdown', this.#handlePointerDown, { passive: true, signal });
    mediaBlock?.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#layoutFrame);
    cancelAnimationFrame(this.#renderFrame);
  }

  get #image() {
    return this.parentElement?.querySelector('img.media-block__media') ?? null;
  }

  #handlePointerDown = (event) => {
    if (event.pointerType !== 'touch') return;

    this.#activeTouchPointerId = event.pointerId;
    this.#updatePointerPosition(event.clientX, event.clientY);
  };

  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch' && event.pointerId !== this.#activeTouchPointerId) return;

    this.#updatePointerPosition(event.clientX, event.clientY);
  };

  #handlePointerUp = (event) => {
    if (event.pointerType !== 'touch' || event.pointerId !== this.#activeTouchPointerId) return;

    this.#activeTouchPointerId = null;
    this.#resetPupils();
  };

  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#resetPupils();
  };

  #updatePointerPosition(x, y) {
    this.#pointerX = x;
    this.#pointerY = y;

    if (this.#renderFrame) return;

    this.#renderFrame = requestAnimationFrame(() => {
      this.#renderFrame = 0;
      this.#renderPupils();
    });
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      this.#resetPupils();
    }
  };

  #queueLayoutUpdate = () => {
    if (this.#layoutFrame) return;

    this.#layoutFrame = requestAnimationFrame(() => {
      this.#layoutFrame = 0;
      this.#updateEyeLayout();
    });
  };

  #updateEyeLayout() {
    const image = this.#image;

    if (!(image instanceof HTMLImageElement)) return;

    const componentRect = this.getBoundingClientRect();
    const imageRect = this.#getRenderedImageRect(image);

    if (!componentRect.width || !componentRect.height || !imageRect.width || !imageRect.height) return;

    const offsetLeft = imageRect.left - componentRect.left;
    const offsetTop = imageRect.top - componentRect.top;
    const eyeWidth = (imageRect.width * this.#clampedDatasetNumber('eyeWidth', 12, 1, 30)) / 100;
    const eyeHeight = (imageRect.height * this.#clampedDatasetNumber('eyeHeight', 12, 1, 30)) / 100;
    const pupilSize = this.#clampedDatasetNumber('pupilSize', 38, 10, 90);

    this.refs.eyes.forEach((eye) => {
      const eyeX = this.#clampedDataAttributeNumber(eye, 'eyeX', 50, 0, 100);
      const eyeY = this.#clampedDataAttributeNumber(eye, 'eyeY', 50, 0, 100);

      eye.style.left = `${offsetLeft + (imageRect.width * eyeX) / 100}px`;
      eye.style.top = `${offsetTop + (imageRect.height * eyeY) / 100}px`;
      eye.style.width = `${eyeWidth}px`;
      eye.style.height = `${eyeHeight}px`;
    });

    this.refs.pupils.forEach((pupil) => {
      pupil.style.width = `${pupilSize}%`;
    });

    this.dataset.ready = 'true';
    this.#renderPupils();
  }

  /**
   * Returns the actual rendered image rectangle after object-fit scaling is applied.
   * This keeps decorative overlays aligned when the image is letterboxed or cropped.
   *
   * @param {HTMLImageElement} image
   * @returns {{ left: number, top: number, width: number, height: number }}
   */
  #getRenderedImageRect(image) {
    const imageRect = image.getBoundingClientRect();
    const naturalWidth = image.naturalWidth || imageRect.width;
    const naturalHeight = image.naturalHeight || imageRect.height;

    if (!naturalWidth || !naturalHeight) {
      return imageRect;
    }

    const objectFit = window.getComputedStyle(image).objectFit || 'fill';
    const widthScale = imageRect.width / naturalWidth;
    const heightScale = imageRect.height / naturalHeight;

    let scale = 1;

    switch (objectFit) {
      case 'contain':
        scale = Math.min(widthScale, heightScale);
        break;
      case 'cover':
        scale = Math.max(widthScale, heightScale);
        break;
      case 'scale-down':
        scale = Math.min(1, Math.min(widthScale, heightScale));
        break;
      case 'none':
        scale = 1;
        break;
      default:
        return imageRect;
    }

    const renderedWidth = naturalWidth * scale;
    const renderedHeight = naturalHeight * scale;
    const offsetLeft = (imageRect.width - renderedWidth) / 2;
    const offsetTop = (imageRect.height - renderedHeight) / 2;

    return {
      left: imageRect.left + offsetLeft,
      top: imageRect.top + offsetTop,
      width: renderedWidth,
      height: renderedHeight,
    };
  }

  #renderPupils() {
    const pupilTravel = this.#clampedDatasetNumber('pupilTravel', 18, 0, 40);

    this.refs.eyes.forEach((eye, index) => {
      const pupil = this.refs.pupils[index];

      if (!(eye instanceof HTMLElement) || !(pupil instanceof HTMLElement)) return;

      const eyeRect = eye.getBoundingClientRect();

      if (!eyeRect.width || !eyeRect.height) return;

      const centerX = eyeRect.left + eyeRect.width / 2;
      const centerY = eyeRect.top + eyeRect.height / 2;
      const deltaX = this.#pointerX - centerX;
      const deltaY = this.#pointerY - centerY;
      const angle = Math.atan2(deltaY, deltaX);
      const maxTravel = (Math.min(eyeRect.width, eyeRect.height) * pupilTravel) / 100;
      const travel = Math.min(
        maxTravel,
        Math.hypot(deltaX, deltaY) * CursorFollowEyesComponent.pointerTravelMultiplier
      );

      eye.style.setProperty('--pupil-x', `${Math.cos(angle) * travel}px`);
      eye.style.setProperty('--pupil-y', `${Math.sin(angle) * travel}px`);
    });
  }

  #resetPupils = () => {
    this.#activeTouchPointerId = null;

    this.refs.eyes.forEach((eye) => {
      if (!(eye instanceof HTMLElement)) return;
      eye.style.setProperty('--pupil-x', '0px');
      eye.style.setProperty('--pupil-y', '0px');
    });
  };

  /**
   * @param {keyof DOMStringMap} name
   * @param {number} fallback
   * @returns {number}
   */
  #numberFromDataset(name, fallback) {
    const value = Number(this.dataset[name]);

    return Number.isFinite(value) ? value : fallback;
  }

  /**
   * @param {keyof DOMStringMap} name
   * @param {number} fallback
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  #clampedDatasetNumber(name, fallback, min, max) {
    return this.#clamp(this.#numberFromDataset(name, fallback), min, max);
  }

  /**
   * @param {HTMLElement} element
   * @param {keyof DOMStringMap} name
   * @param {number} fallback
   * @returns {number}
   */
  #numberFromDataAttribute(element, name, fallback) {
    const value = Number(element.dataset[name]);

    return Number.isFinite(value) ? value : fallback;
  }

  /**
   * @param {HTMLElement} element
   * @param {keyof DOMStringMap} name
   * @param {number} fallback
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  #clampedDataAttributeNumber(element, name, fallback, min, max) {
    return this.#clamp(this.#numberFromDataAttribute(element, name, fallback), min, max);
  }

  /**
   * @param {number} value
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  #clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}

if (!customElements.get('cursor-follow-eyes-component')) {
  customElements.define('cursor-follow-eyes-component', CursorFollowEyesComponent);
}
